import React, { useState, useEffect, useRef } from "react";
import { getISTCalendarDate, getISTDateInputValue } from "../utils/dateTime";

function AddStockModal({ show, onClose, initialBarcode = "" }) {
    const [editingBatch, setEditingBatch] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [success, setSuccess] = useState("");
    const barcodeRef = useRef(null);
    const lookupTimerRef = useRef(null);
    const [form, setForm] = useState({
        barcode: "",
        name: "",
        quantity: "",
        batchNumber: "",
        expiryDate: "",
        price: "",
        gstRate: "",
        itemDiscount: "",
        category: ""
    });
    const [noExpiry, setNoExpiry] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [existingBatches, setExistingBatches] = useState([]);
    const [suggestedBatch, setSuggestedBatch] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const formatLocalDate = (date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    };

    const getAccessoryExpiryDate = () => {
        const expiry = getISTCalendarDate();
        expiry.setFullYear(expiry.getFullYear() + 2);
        return formatLocalDate(expiry);
    };

    const isNumericString = (value) => /^[0-9]*$/.test(value);

    const isDecimalString = (value) => /^[0-9]*\.?[0-9]*$/.test(value);

    // 🔹 Generate batch like B18042026
    const generateBatch = () => {
        const [year, month, day] = getISTDateInputValue().split("-");
        const batch = `B${day}${month}${year}`;

        return batch;
    };
    

    // 🔹 Reset when modal opens
    useEffect(() => {
        if (show) {
            setForm({
                barcode: initialBarcode || "",
                name: "",
                quantity: "",
                batchNumber: "",
                expiryDate: "",
                price: "",
                gstRate: "",
                itemDiscount: "",
                category: ""
            });
            setNoExpiry(false);
            setFieldErrors({});
            setExistingBatches([]);
            setSuggestedBatch("");
            setError("");
            setSuccess("");
            window.clearTimeout(lookupTimerRef.current);
            setTimeout(() => barcodeRef.current?.focus(), 0);
        }
    }, [show, initialBarcode]);

    useEffect(() => {
        if (show && initialBarcode) {
            setForm(prev => ({
                ...prev,
                barcode: initialBarcode
            }));
        }
    }, [show, initialBarcode]);

    const handleEditClick = (batch) => {
        setEditingBatch(batch);

        setEditForm({
            id: batch.id, // ✅ must exist
            name: form.name || "", // from product
            batchNumber: batch.batchNumber || "",
            quantity: batch.quantity || "",
            price: batch.price || "",
            expiryDate: batch.expiryDate || "",
            itemDiscount: batch.itemDiscount || "",
            category: batch.category || form.category || "",
            gstRate: form.gstRate || ""
        });
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;

        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateBatch = async () => {
        try {
            setLoading(true);
            setError("");
            setSuccess("");

            const token = localStorage.getItem("token");

            const payload = {
                name: editForm.name,
                batchNumber: editForm.batchNumber,
                quantity: parseInt(editForm.quantity || 0),
                price: parseFloat(editForm.price || 0),
                expiryDate: editForm.expiryDate,
                itemDiscount: parseFloat(editForm.itemDiscount || 0),
                category: editForm.category,
                gstRate: editForm.gstRate ? Number(editForm.gstRate) : 0
            };

            console.log("🔥 Sending payload:", payload);

            const res = await fetch(
                `/v1/stocks/${editForm.id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!res.ok) {
                throw new Error("Update failed");
            }

            setSuccess("Stock updated successfully ✅");

            // 🔥 refresh batches properly
            await fetchProductDetails();

            setEditingBatch(null);

        } catch (err) {
            console.error(err);
            setError("Failed to update stock");
        } finally {
            setLoading(false);
        }
    };


    const fetchProductDetails = async (barcodeValue = form.barcode) => {
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `/v1/stocks/product/${barcodeValue}`,
                {
                    headers: { Authorization: "Bearer " + token }
                }
            );

            if (!res.ok) return;

            const data = await res.json();

            // ✔ Autofill name if exists
            if (data.productName) {
                setForm(prev => ({
                    ...prev,
                    name: data.productName,
                    category: data.category || prev.category,
                    gstRate: data.gstRate != null ? String(data.gstRate) : ""
                }));
            }

            // ✔ Existing batches
            setExistingBatches(data.batches || []);

            // ✔ Suggest batch
            const newBatch = generateBatch();
            setSuggestedBatch(newBatch);

            setForm(prev => ({
                ...prev,
                batchNumber: newBatch
            }));

        } catch (err) {
            console.error(err);
        }
    };

    // 🔹 Fetch product details when barcode changes
    useEffect(() => {
        window.clearTimeout(lookupTimerRef.current);

        if (String(form.barcode || "").trim().length < 1) return undefined;

        lookupTimerRef.current = window.setTimeout(() => {
            fetchProductDetails(form.barcode);

            const newBatch = generateBatch();
            setSuggestedBatch(newBatch);

            setForm(prev => ({
                ...prev,
                batchNumber: newBatch
            }));
        }, 350);

        return () => window.clearTimeout(lookupTimerRef.current);

    }, [form.barcode]);

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "quantity") {
            if (!isNumericString(value)) return;
        }

        if (name === "price" || name === "itemDiscount") {
            if (!isDecimalString(value)) return;
        }

        if (name === "gstRate") {
            if (!isNumericString(value)) return;
        }

        if (name === "category") {
            setNoExpiry(false);
        }

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        setFieldErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    const handleNoExpiryChange = (e) => {
        const checked = e.target.checked;
        const expiryDate = checked ? getAccessoryExpiryDate() : "";

        setNoExpiry(checked);
        setForm(prev => ({
            ...prev,
            expiryDate
        }));
        setFieldErrors(prev => ({
            ...prev,
            expiryDate: ""
        }));
    };

    const validateForm = () => {
        const errors = {};

        if (!form.barcode.trim()) errors.barcode = "Barcode is required";
        if (!form.name.trim()) errors.name = "Product name is required";
        if (!form.category.trim()) errors.category = "Category is required";
        if (!form.quantity.trim()) {
            errors.quantity = "Quantity is required";
        } else if (!/^[0-9]+$/.test(form.quantity)) {
            errors.quantity = "Quantity must contain only numbers";
        }

        if (!form.price.trim()) {
            errors.price = "Price is required";
        } else if (!/^[0-9]+(\.[0-9]+)?$/.test(form.price)) {
            errors.price = "Price must contain only numbers";
        }

        if (!form.gstRate.trim()) {
            errors.gstRate = "GST rate is required";
        } else if (!/^[0-9]+$/.test(form.gstRate)) {
            errors.gstRate = "GST rate must contain only numbers";
        }

        if (!form.itemDiscount.trim()) {
            errors.itemDiscount = "Item discount is required";
        } else if (!/^[0-9]+(\.[0-9]+)?$/.test(form.itemDiscount)) {
            errors.itemDiscount = "Item discount must contain only numbers";
        }

        if (noExpiry && form.category === "accessories" && !form.expiryDate) {
            errors.expiryDate = "Expiry date was not set";
        }

        return errors;
    };

    // 🔹 Submit
    const handleSubmit = async () => {
        setError("");
        setSuccess("");

        const validationErrors = validateForm();
        setFieldErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setError("Please fill all required fields");
            return;
        }

        const finalExpiryDate =
            form.category === "accessories" && noExpiry
                ? getAccessoryExpiryDate()
                : form.expiryDate;

        try {
            setLoading(true);

            const token = localStorage.getItem("token");

            const res = await fetch("/v1/stocks", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({
                    barcode: form.barcode,
                    name: form.name,
                    quantity: Number(form.quantity),
                    category: form.category,
                    batchNumber: form.batchNumber,
                    itemDiscount: Number(form.itemDiscount),
                    expiryDate: finalExpiryDate,
                    price: Number(form.price),
                    gstRate: form.gstRate ? Number(form.gstRate) : 0
                })
            });

            let data = {};
            try {
                data = await res.json();
            } catch { }

            if (!res.ok) {
                setError(data.message || "Failed to add stock");
                return;
            }

            setSuccess("Stock added successfully ✅");

            // reset
            setForm({
                barcode: "",
                name: "",
                quantity: "",
                batchNumber: "",
                itemDiscount: "",
                expiryDate: "",
                price: "",
                gstRate: "",
                category: ""
            });

            setNoExpiry(false);
            setExistingBatches([]);
            setSuggestedBatch("");
            setFieldErrors({});

            setTimeout(() => barcodeRef.current?.focus(), 0);


        } catch (err) {
            console.error(err);
            setError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show fade d-block" tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5 className="modal-title">Add Stock</h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <div className="modal-body">

                            {error && (
                                <div className="alert alert-danger p-2">
                                    {error}
                                </div>
                            )}

                            {success && (
                                <div className="alert alert-success p-2 text-center">
                                    {success}
                                </div>
                            )}

                            <div className="container-fluid">

                                {/* 🔹 FULL WIDTH ROWS */}
                                <div className="row mb-2">
                                    <div className="col-12">
                                        <input
                                            ref={barcodeRef}
                                            className="form-control"
                                            name="barcode"
                                            placeholder="Scan Barcode"
                                            value={form.barcode}
                                            onChange={handleChange}
                                            autoFocus
                                        />
                                        {fieldErrors.barcode && (
                                            <small className="text-danger">{fieldErrors.barcode}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-12">
                                        <select
                                            className="form-control"
                                            name="category"
                                            value={form.category}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Category</option>
                                            <option value="food">Food</option>
                                            <option value="medicine">Medicine</option>
                                            <option value="accessories">Accessories</option>
                                            <option value="services">Services</option>
                                            <option value="lab-test">Lab Test</option>
                                            <option value="beauty-cosmetics">Beauty & Consmetics</option>
                                        </select>
                                        {fieldErrors.category && (
                                            <small className="text-danger">{fieldErrors.category}</small>
                                        )}
                                    </div>
                                </div>

                                {existingBatches.length > 0 && (
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <small className="fw-bold text-primary">Existing Batches</small>

                                            <div
                                                className="p-2 rounded"
                                                style={{
                                                    maxHeight: "200px",
                                                    overflowY: "auto",
                                                    backgroundColor: "#f8f9ff",
                                                    border: "1px solid #cfe2ff"
                                                }}
                                            >
                                                {existingBatches.map((b, i) => (
                                                    <div
                                                        key={i}
                                                        className="mb-2 p-2 border rounded bg-white"
                                                    >

                                                        {/* NORMAL VIEW */}
                                                        {editingBatch?.id !== b.id && (
                                                            <>
                                                                <div style={{ fontWeight: "500" }}>
                                                                    {b.batchNumber}
                                                                </div>

                                                                <div style={{ fontSize: "12px" }}>
                                                                    ₹{b.price} • Qty: {b.quantity}
                                                                </div>

                                                                <button
                                                                    className="btn btn-sm btn-outline-primary mt-1"
                                                                    onClick={() => handleEditClick(b)}
                                                                >
                                                                    Edit
                                                                </button>
                                                            </>
                                                        )}

                                                        {/* EDIT MODE */}

                                                        {editingBatch?.id === b.id && (
                                                            <div
                                                                className="mt-2 p-2 rounded"
                                                                style={{ background: "#f8f9fa", border: "1px solid #dee2e6" }}
                                                            >

                                                                {/* 🔹 Header */}
                                                                <div className="mb-2 text-primary fw-bold" style={{ fontSize: "13px" }}>
                                                                    {b.batchNumber}
                                                                </div>

                                                                <div className="row g-2">

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Product Name</label>
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            name="name"
                                                                            value={editForm.name}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Batch Name / Batch Number</label>
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            name="batchNumber"
                                                                            value={editForm.batchNumber || ""}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-12">
                                                                        <small className="text-muted">
                                                                            Batch ID: {b.id} | Current Batch: {b.batchNumber || "-"}
                                                                        </small>
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Quantity</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm"
                                                                            name="quantity"
                                                                            value={editForm.quantity}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Price (₹)</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm"
                                                                            name="price"
                                                                            value={editForm.price}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">GST Rate (%)</label>
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            name="gstRate"
                                                                            value={editForm.gstRate || ""}
                                                                            onChange={handleEditChange}
                                                                        >
                                                                            <option value="">Select GST %</option>
                                                                            <option value="0">0%</option>
                                                                            <option value="5">5%</option>
                                                                            <option value="9">9%</option>
                                                                            <option value="12">12%</option>
                                                                            <option value="18">18%</option>
                                                                        </select>
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Expiry Date</label>
                                                                        <input
                                                                            type="date"
                                                                            className="form-control form-control-sm"
                                                                            name="expiryDate"
                                                                            value={editForm.expiryDate || ""}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Discount (%)</label>
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            name="itemDiscount"
                                                                            value={editForm.itemDiscount}
                                                                            onChange={handleEditChange}
                                                                        />
                                                                    </div>

                                                                    <div className="col-6">
                                                                        <label className="form-label small mb-1">Category</label>
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            name="category"
                                                                            value={editForm.category}
                                                                            onChange={handleEditChange}
                                                                        >
                                                                            <option value="">Select</option>
                                                                            <option value="food">Food</option>
                                                                            <option value="medicine">Medicine</option>
                                                                            <option value="accessories">Accessories</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* 🔹 Actions */}
                                                                    <div className="col-12 d-flex gap-2 mt-2">
                                                                        <button
                                                                            className="btn btn-success btn-sm"
                                                                            onClick={handleUpdateBatch}
                                                                        >
                                                                            Update
                                                                        </button>

                                                                        <button
                                                                            className="btn btn-secondary btn-sm"
                                                                            onClick={() => setEditingBatch(null)}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        )}

                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 🔹 BALANCED 2-COLUMN GRID */}
                                <div className="row g-2">

                                    {/* LEFT */}
                                    <div className="col-md-6">
                                            <input
                                                className="form-control"
                                                name="name"
                                                placeholder="Product Name"
                                                value={form.name}
                                                onChange={handleChange}
                                            />
                                            {fieldErrors.name && (
                                                <small className="text-danger">{fieldErrors.name}</small>
                                            )}
                                        </div>

                                        {/* RIGHT */}
                                        <div className="col-md-6">
                                            <input
                                            type="number"
                                            className="form-control"
                                                name="quantity"
                                                placeholder="Quantity"
                                                value={form.quantity}
                                                onChange={handleChange}
                                            />
                                            {fieldErrors.quantity && (
                                                <small className="text-danger">{fieldErrors.quantity}</small>
                                            )}
                                        </div>

                                        {/* LEFT */}
                                        <div className="col-md-6">
                                            <input
                                            type="number"
                                            className="form-control"
                                                name="price"
                                                placeholder="Product Price"
                                                value={form.price}
                                                onChange={handleChange}
                                            />
                                            {fieldErrors.price && (
                                                <small className="text-danger">{fieldErrors.price}</small>
                                            )}
                                        </div>

                                        {/* RIGHT */}
                                        <div className="col-md-6">
                                        <select
                                            className="form-control"
                                            name="gstRate"
                                            value={form.gstRate}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select GST %</option>
                                            <option value="0">0%</option>
                                            <option value="5">5%</option>
                                            <option value="9">9%</option>
                                                <option value="12">12%</option>
                                                <option value="18">18%</option>
                                            </select>
                                            {fieldErrors.gstRate && (
                                                <small className="text-danger">{fieldErrors.gstRate}</small>
                                            )}
                                            {form.gstRate !== "" && (
                                                <small className="text-muted d-block mt-1">
                                                    Loaded GST rate from backend: {form.gstRate}%
                                                </small>
                                            )}
                                        </div>

                                        {/* LEFT */}
                                        <div className="col-md-6">
                                            <input
                                            className="form-control"
                                                name="batchNumber"
                                                placeholder="Batch Number"
                                                value={form.batchNumber}
                                                onChange={handleChange}
                                            />
                                        {suggestedBatch && (
                                            <small className="text-success">
                                                Suggested: {suggestedBatch}
                                            </small>
                                        )}
                                    </div>

                                    {/* RIGHT */}
                                    <div className="col-md-6">
                                            <input
                                            type="text"
                                                className="form-control"
                                                name="itemDiscount"
                                                placeholder="Item Discount %"
                                                value={form.itemDiscount}
                                                onChange={handleChange}
                                            />
                                            {fieldErrors.itemDiscount && (
                                                <small className="text-danger">{fieldErrors.itemDiscount}</small>
                                            )}
                                        </div>

                                </div>

                                {form.category === "accessories" && (
                                    <div className="row mt-2">
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="noExpiryCheck"
                                                    checked={noExpiry}
                                                    onChange={handleNoExpiryChange}
                                                />
                                                <label className="form-check-label" htmlFor="noExpiryCheck">
                                                    No expiry for accessories
                                                </label>
                                            </div>
                                            {noExpiry && (
                                                <small className="text-muted d-block mt-1">
                                                    Expiry will be set to 2 years from today.
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* 🔹 FULL WIDTH EXPIRY AT BOTTOM */}
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="expiryDate"
                                            value={form.expiryDate}
                                            onChange={handleChange}
                                            disabled={noExpiry && form.category === "accessories"}
                                        />
                                        {fieldErrors.expiryDate && (
                                            <small className="text-danger">{fieldErrors.expiryDate}</small>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Saving..." : "Add Stock"}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default AddStockModal;
